import React, { useState } from "react";
import { ThumbsUp, ThumbsDown, Loader2, Check, MessageSquarePlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFeedbackSubmit } from "@/components/gooseberry-output/useFeedbackSubmit";

const copy = {
  nl: {
    marked: "Je hebt deze sectie gemarkeerd als",
    correct: "kloppend",
    needsCorrection: "moet aangepast worden",
    change: "Feedback aanpassen",
    question: "Klopt deze sectie?",
    up: "Klopt",
    down: "Niet helemaal",
    label: "Wat klopt er niet of wat ontbreekt? (optioneel, maar helpt het model om dit te verbeteren)",
    placeholder: "Bijv. De persona past niet bij de echte doelgroep van sociaal werkers.",
    cancel: "Annuleren",
    send: "Correctie versturen",
    mark: "Markeren zonder notitie",
  },
  en: {
    marked: "You marked this section as",
    correct: "correct",
    needsCorrection: "needs correction",
    change: "Change my feedback",
    question: "Does this section look right?",
    up: "Correct",
    down: "Not quite",
    label: "What is wrong or missing? (optional but helps the model fix it)",
    placeholder: "e.g. The persona doesn't match the actual target group of social workers.",
    cancel: "Cancel",
    send: "Send correction",
    mark: "Mark without note",
  },
};

export function SectionFeedbackBar({
  runId,
  sectionKey,
  sectionTitle,
  projectName,
  mode,
  existing,
  onSubmitted,
  language = "en",
}) {
  const { pendingVote, submitted, setSubmitted, submit } = useFeedbackSubmit({
    runId,
    sectionKey,
    sectionTitle,
    projectName,
    mode,
    language,
    existing,
    onSubmitted,
  });
  const t = copy[language] || copy.en;
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [comment, setComment] = useState("");

  async function sendDown() {
    await submit("down", comment.trim());
    setShowCommentBox(false);
    setComment("");
  }

  if (submitted) {
    return (
      <SubmittedConfirmation
        sectionKey={sectionKey}
        submitted={submitted}
        onChange={() => setSubmitted(null)}
        t={t}
      />
    );
  }

  return (
    <div
      className="mt-4 rounded-xl border border-black/10 bg-white/70 p-3"
      data-testid={`gooseberry-feedback-bar-${sectionKey}`}
    >
      <VoteRow
        sectionKey={sectionKey}
        pendingVote={pendingVote}
        onUp={() => submit("up", "")}
        onDownStart={() => setShowCommentBox(true)}
        t={t}
      />
      {showCommentBox && (
        <CommentBox
          sectionKey={sectionKey}
          comment={comment}
          onChange={(v) => setComment(v.slice(0, 4000))}
          onCancel={() => { setShowCommentBox(false); setComment(""); }}
          onSubmit={sendDown}
          submitting={pendingVote === "down"}
          t={t}
        />
      )}
    </div>
  );
}

function SubmittedConfirmation({ sectionKey, submitted, onChange, t }) {
  return (
    <div
      className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#61CBA3]/40 bg-[rgba(97,203,163,0.08)] px-3 py-2 text-xs text-black/75"
      data-testid={`gooseberry-feedback-bar-${sectionKey}`}
    >
      <div className="flex items-center gap-2">
        <Check className="h-3.5 w-3.5 text-[#61CBA3]" />
        <span>
          {t.marked}{" "}
          <strong className="text-black">
            {submitted.vote === "up" ? t.correct : t.needsCorrection}
          </strong>
          .
        </span>
        {submitted.vote === "down" && submitted.comment && (
          <span className="hidden sm:inline text-black/55 italic truncate max-w-[260px]">
            "{submitted.comment}"
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={onChange}
        className="text-[#61CBA3] hover:text-[#4DAF8B] underline underline-offset-4"
        data-testid={`gooseberry-feedback-change-${sectionKey}`}
      >
        {t.change}
      </button>
    </div>
  );
}

function VoteRow({ sectionKey, pendingVote, onUp, onDownStart, t }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="text-xs text-black/65">{t.question}</div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-full border-black/15 h-8 px-3 text-xs hover:border-[#61CBA3] hover:text-[#4DAF8B]"
          onClick={onUp}
          disabled={!!pendingVote}
          data-testid={`gooseberry-feedback-up-${sectionKey}`}
        >
          {pendingVote === "up" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <ThumbsUp className="h-3.5 w-3.5" />
          )}
          <span className="ml-1.5">{t.up}</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-full border-black/15 h-8 px-3 text-xs hover:border-[#61CBA3] hover:text-[#4DAF8B]"
          onClick={onDownStart}
          disabled={!!pendingVote}
          data-testid={`gooseberry-feedback-down-${sectionKey}`}
        >
          <ThumbsDown className="h-3.5 w-3.5" />
          <span className="ml-1.5">{t.down}</span>
        </Button>
      </div>
    </div>
  );
}

function CommentBox({ sectionKey, comment, onChange, onCancel, onSubmit, submitting, t }) {
  return (
    <div className="mt-3">
      <label
        htmlFor={`fb-comment-${sectionKey}`}
        className="flex items-center gap-1.5 text-xs text-black/65 mb-1.5"
      >
        <MessageSquarePlus className="h-3.5 w-3.5" />
        {t.label}
      </label>
      <textarea
        id={`fb-comment-${sectionKey}`}
        value={comment}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        placeholder={t.placeholder}
        className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm focus:outline-none focus:border-[#61CBA3] focus:ring-2 focus:ring-[rgba(97,203,163,0.25)]"
        data-testid={`gooseberry-feedback-comment-${sectionKey}`}
      />
      <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="rounded-full h-8 px-3 text-xs text-black/60"
          onClick={onCancel}
          disabled={submitting}
          data-testid={`gooseberry-feedback-cancel-${sectionKey}`}
        >
          <X className="h-3.5 w-3.5 mr-1" /> {t.cancel}
        </Button>
        <Button
          type="button"
          size="sm"
          className="rounded-full h-8 px-4 text-xs bg-[#61CBA3] text-white hover:bg-[#4DAF8B]"
          onClick={onSubmit}
          disabled={submitting}
          data-testid={`gooseberry-feedback-submit-${sectionKey}`}
        >
          {submitting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
          ) : null}
          {comment.trim() ? t.send : t.mark}
        </Button>
      </div>
    </div>
  );
}
